/**
 * Captura de erros — o modelo "relatório de erros do Windows".
 *
 * CAPTURA SEMPRE, silenciosamente. AVISA só se a pessoa quiser.
 *
 * Foi o Lucas quem escolheu esse desenho no sistema atual, e ele está certo
 * pelo motivo que quase todo sistema descobre tarde: **erro que depende de
 * alguém reportar é erro que ninguém reporta**. A pessoa em obra, com o
 * celular na mão, fecha o aviso e continua o trabalho — e o problema fica
 * invisível até virar reclamação.
 *
 * Então o registro é automático. O que a pessoa decide é só se quer contar o
 * que estava fazendo — e isso vale muito, porque a metade difícil de
 * reproduzir um erro é saber o que a pessoa tentou.
 */

import { api } from '../api.js';

let moduloAtual = 'sistema';

/** O roteador diz em que módulo estamos, para o erro saber de onde veio. */
export function moduloEmUso(chave: string): void {
  moduloAtual = chave;
}

/**
 * Evita a enxurrada: um erro que se repete em laço registraria centenas de
 * linhas iguais e enterraria o resto. A mesma mensagem só é registrada uma
 * vez por minuto.
 */
const vistos = new Map<string, number>();

function repetido(chave: string): boolean {
  const agora = Date.now();
  const ultimo = vistos.get(chave);
  if (ultimo && agora - ultimo < 60_000) return true;
  vistos.set(chave, agora);
  return false;
}

export async function registrarErro(
  mensagem: string,
  acao?: string,
  contexto?: Record<string, unknown>,
  reportado = false,
): Promise<void> {
  if (repetido(mensagem + (acao ?? ''))) return;
  try {
    await api.post('/api/erros', {
      modulo: moduloAtual,
      ...(acao ? { acao } : {}),
      mensagem: mensagem.slice(0, 2000),
      ...(contexto ? { contexto } : {}),
      reportado,
    });
  } catch {
    /**
     * Registrar o erro falhou. Não há o que fazer aqui, e insistir seria pior:
     * uma tentativa de registrar a falha do registro entra em laço. O erro
     * original já foi mostrado à pessoa pela tela que o pegou.
     */
  }
}

/**
 * Mostra o erro à pessoa, registra sozinho, e oferece contar o que aconteceu.
 *
 * As telas chamam isto em vez de `alert()` — o alerta continua igual para
 * quem vê, e o registro acontece de qualquer forma.
 */
export function avisarErro(erro: unknown, acao?: string): void {
  const mensagem = erro instanceof Error ? erro.message : String(erro);
  alert(mensagem);
  void registrarErro(mensagem, acao, {
    pagina: location.hash || location.pathname,
    ...(erro instanceof Error && erro.stack ? { pilha: erro.stack.slice(0, 1500) } : {}),
  });
}

/**
 * Liga a captura do que ninguém tratou.
 *
 * `error` pega exceção solta; `unhandledrejection` pega promessa que falhou
 * sem `catch` — que é a forma mais comum num sistema feito de chamadas à API,
 * e a que passa despercebida porque não quebra a tela.
 */
export function instalarCapturaDeErros(): void {
  window.addEventListener('error', (e) => {
    void registrarErro(e.message, 'erro não tratado', {
      arquivo: e.filename,
      linha: e.lineno,
      ...(e.error instanceof Error && e.error.stack ? { pilha: e.error.stack.slice(0, 1500) } : {}),
    });
  });

  window.addEventListener('unhandledrejection', (e) => {
    const r = e.reason;
    void registrarErro(
      r instanceof Error ? r.message : String(r),
      'promessa não tratada',
      r instanceof Error && r.stack ? { pilha: r.stack.slice(0, 1500) } : undefined,
    );
  });
}
